import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ListCreateButton from 'components/buttons/ListCreateButton';
import {
    ArrowOutwardRounded as ArrowOutwardRoundedIcon,
    AttachMoney,
    Block as BlockIcon,
    BusinessCenter,
    GroupOutlined as GroupOutlinedIcon,
    InfoOutlined as InfoOutlinedIcon,
    Person,
} from 'components/icons';
import ExclamationMarkIcon from 'assets/images/icons/ExclamationMarkIcon.svg?react';
import Chip from 'components/@extended/Chip';
import SegmentChip from 'components/@extended/SegmentChip.tsx';
import DmsLink from 'components/dmslink/DmsLink.tsx';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftRowMenu } from 'components/drafts/DraftRecordActions';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import Loader from 'components/Loader';
import RelationshipPartnersAndManagers from 'components/misc/RelationshipPartnersAndManagers.tsx';
import { OpponentChip } from 'components/party/OpponentChip';
import PartyName from 'components/party/PartyName';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import type { CellContext } from '@tanstack/react-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { mapToFilterOptions } from 'components/tables/list-table/filterUtil.ts';
import { FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { Checkbox } from 'components/ui/checkbox';
import { Tooltip } from 'components/ui/tooltip';
import CopyToClipboard from 'components/util/CopyToClipboard';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useHasSeveralActiveOffices } from 'hooks/useHasSeveralActiveOffices';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLastNameFirst } from 'utils/nameOrder';
import {
    useLazyGetContactsCountQuery,
    useLazyGetContactsWithFiltersQuery,
    useLazyGetMinContactsListWithFiltersQuery,
} from 'store/api/contacts';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useStartPartyExportMutation } from 'store/api/exports';
import { drawsOwnFailure } from 'store/api/portalAPI';
import { VerificationStatusType } from 'types/compliance.ts';
import {
    ContactListItem,
    PartyRelationshipTypeCode,
    PartyToPartyRelationDto,
    PartyType,
    RelationshipTypeCode,
} from 'types/contacts';
import { EXPORT_CONFIG } from 'types/exports';
import { FeatureFlag } from 'types/features.ts';
import { ReferralTypeCode } from 'types/referrals.ts';
import { errorAlert } from 'utils/alert';
import { getClassifierText, t } from 'utils/getTranslations';
import { useLastEffortType } from 'hooks/useLastEffortType';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useAsyncExport } from 'utils/useAsyncExport.ts';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import ContactsSelectionBar from './ContactsSelectionBar';
import { contactFilters } from './contactFilters';
import { calculateTimeSince, TimeSinceCell } from './util';
import { Setting } from 'types/back-office-settings';
import { useSettings } from 'utils/useSettings';
import { useAdministratorField } from 'hooks/useAdministratorField';

const USED_CLASSIFIERS = [
    'RELATIONSHIP_TYPE',
    'PARTY_STATUS',
    'LANGUAGE',
    'BUSINESS_AREA_TYPE',
    'COUNTRY',
    'MATTER_STATE_TYPE',
    'SERVICE_LEVEL_TYPE',
    'SEGMENT_TYPE',
    'MAILING_PREFERENCE_TYPE',
    'MARKETING_AREA_TYPE',
    'AREA_OF_RESPONSIBILITY_TYPE',
    'KEYWORD_TYPE',
    'COOPERATION_AREA_TYPE',
    'REFERRAL_TYPE',
];

const MERGE_PERMISSIONS = ['UPDATE.PARTIES.MERGE_CONTACTS'];
const REFERRAL_READ_PERMISSIONS = ['READ.REFERRALS.ALL'];

// `entity-link` hands the cell's link colour and wrapping to the table's own rule.
// `wrap-anywhere`, in the new design: an address longer than its column breaks onto a second line, where it
// would be clipped.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline new:wrap-anywhere';

const filterRelationshipTypes = [
    RelationshipTypeCode.CLIENT,
    RelationshipTypeCode.LEAD,
    RelationshipTypeCode.NON_CLIENT,
];

const filterReferredBySourceTypes = [ReferralTypeCode.CONTACT, ReferralTypeCode.EMPLOYEE, ReferralTypeCode.OTHER];

type Props = {
    disableExport?: boolean;
};

/** The list's count, with the drafts the list shows. */
const useLazyContactsCountQuery: typeof useLazyGetContactsCountQuery = (options) => {
    const [fetchCount, ...rest] = useLazyGetContactsCountQuery(options);
    return [useWithDrafts(fetchCount), ...rest];
};

const getContactHref = (contact: ContactListItem) => `/contact/${contact.id}`;

const ContactsTable = ({ disableExport = false }: Props) => {
    const navigateWithReturn = useNavigateWithReturnState();
    const isNewDesign = useIsNewDesign();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const hasPermissionToExport = useHasPermissions(['EXPORT.PARTIES']);
    const { isSettingEnabled } = useSettings();
    const lastEffortType = useLastEffortType();
    const hasSeveralActiveOffices = useHasSeveralActiveOffices();
    const isNonVerifiedIconEnabled = isSettingEnabled(Setting.NON_VERIFIED_CLIENT_ICON);
    const isInvoicingDateOnPartyFormEnabled = useIsFeatureEnabled(FeatureFlag.INVOICING_DATE_ON_PARTY_FORM);
    const { isOn: isAdministratorFieldEnabled, clientLabel: clientAssistantLabel } = useAdministratorField();
    const isContactMergingEnabled = useIsFeatureEnabled(FeatureFlag.CONTACT_MERGING);
    const hasPermissionToMerge = useHasPermissions(MERGE_PERMISSIONS);
    const canMergeContacts = isContactMergingEnabled && hasPermissionToMerge;
    // The backend refuses the referral filters to a user who may not read referrals.
    const canReadReferrals = useHasPermissions(REFERRAL_READ_PERMISSIONS);
    const lastNameFirst = useLastNameFirst();
    const dateFormat = useEffectiveDateFormat();
    const [fetchContactsOnly, { data, isFetching, error, isError }] = useLazyGetContactsWithFiltersQuery();
    const fetchContacts = useWithDrafts(fetchContactsOnly);
    // The new design draws a failed list with no rows to keep as its error state, which says it all: that request
    // raises no toast. Over rows that stay on screen (another filter, a further page) the toast is all there is.
    const hasRows = useRef(false);
    useEffect(() => {
        hasRows.current = !!data?.content.length;
    }, [data]);
    const fetchData = useCallback<typeof fetchContacts>(
        (arg, preferCacheValue) => {
            const request = fetchContacts(arg, preferCacheValue);
            if (isNewDesign && !hasRows.current) {
                drawsOwnFailure(request.requestId);
            }
            return request;
        },
        [fetchContacts, isNewDesign]
    );

    const { isBusy: isExportDataFetching, triggerManualDownload } = useAsyncExport(useStartPartyExportMutation, {
        entityType: 'PARTY',
        entityName: t('contacts.export.entity-name'),
        fileName: EXPORT_CONFIG.PARTY.fileName,
    });

    function getEmployers(original_cell: ContactListItem | [] | null | undefined): PartyToPartyRelationDto[] {
        let employees: PartyToPartyRelationDto[] = [];
        if (!original_cell || Array.isArray(original_cell) || !original_cell.partyType) {
            return [];
        }
        // Filter relatesTo by partyType. PartyRelationshipTypeCode only contains
        // EMPLOYEE/PRIMARY_CONTACT/SECONDARY_RECIPIENT, so the 'PERSON' comparison never matches and
        // the COMPANY branch yields nothing; the string cast is what lets it compile.
        if (original_cell.partyType === 'COMPANY') {
            employees = (original_cell.relatesTo ?? []).filter(
                (item) => (item.partyRelationshipTypeCode as string) === 'PERSON'
            );
        } else if (original_cell.partyType === 'PERSON') {
            employees = (original_cell.relatesTo ?? []).filter(
                (item) => item.partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE
            );
        }
        return employees;
    }

    // Each ticked contact's type is kept from the moment it is ticked, for the bar. The table unticks
    // a row once the visible rows no longer hold it, so both ticks are on screen together.
    const [selected, setSelected] = useState(() => new Map<number, PartyType>());
    const selectedIds = useMemo(() => new Set(selected.keys()), [selected]);
    const handleSelectionChange = useCallback(
        (visibleSelection: Set<number>) =>
            setSelected((previous) => new Map([...previous].filter(([id]) => visibleSelection.has(id)))),
        []
    );
    const toggleSelected = useCallback(
        ({ id, partyType }: ContactListItem) =>
            setSelected((previous) => {
                const next = new Map(previous);
                if (!next.delete(id)) next.set(id, partyType);
                return next;
            }),
        []
    );
    const clearSelection = useCallback(() => setSelected(new Map()), []);

    // Classic's own toast; in the new design the API error handler's is the one toast of a failure.
    if (error && !isNewDesign) {
        errorAlert(t('contacts.error-loading-contacts-list'));
    }

    const columns = useMemo<DataTableColumnDef<ContactListItem>[]>(
        () => [
            {
                header: t('name'),
                id: 'name',
                accessorKey: 'name',
                meta: {
                    // Flip the server sort key between first and last name per the LAST_NAME_FIRST flag,
                    // matching the displayed order (PartyName renders last-name-first under the flag).
                    // Companies have no last name; they sort on their empty last name under the flag.
                    sortAccessor: lastNameFirst ? 'lastName' : 'firstName',
                    widthWeight: 2.2,
                    minPx: 240,
                },
                cell: ({ row }) => {
                    const contactListItem = row.original;
                    return (
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <PartyName
                                    party={contactListItem}
                                    isLink
                                    plainLink
                                    variant="list"
                                    showPartyNumber
                                    className="font-semibold"
                                />
                                {contactListItem.officialName &&
                                    contactListItem.officialName !== contactListItem.name && (
                                        <Tooltip content={contactListItem.officialName}>
                                            <span
                                                tabIndex={0}
                                                role="img"
                                                aria-label={contactListItem.officialName}
                                                className="inline-flex"
                                            >
                                                <InfoOutlinedIcon className="text-grey-600 h-[1.3rem]" />
                                            </span>
                                        </Tooltip>
                                    )}
                                {/* The new design keeps the DMS link beside the name, as the case cell does, so
                                    the chips close the cell. A draft shows none. */}
                                {isNewDesign && contactListItem.dmsLink && !contactListItem.isDraft ? (
                                    <DmsLink link={contactListItem.dmsLink} variant="icon-chip" />
                                ) : null}
                                {contactListItem.isDraft ? (
                                    <DraftRowMenu
                                        draft={{ type: 'contact', id: contactListItem.id }}
                                        className="size-7"
                                    />
                                ) : null}
                            </div>
                            {/* In the new design a group that does not fit moves to the next line whole, never cut. */}
                            <div className="new:flex-wrap new:gap-y-1 flex gap-2">
                                <div className="flex gap-0.5">
                                    {contactListItem.isDraft ? <DraftChip /> : null}
                                    {isNonVerifiedIconEnabled &&
                                        contactListItem.relationshipTypeCode === RelationshipTypeCode.CLIENT &&
                                        contactListItem.complianceVerificationStatusTypeCode ===
                                            VerificationStatusType.NOT_VERIFIED && (
                                            <Tooltip content={t('compliance.not-verified')}>
                                                <div>
                                                    <Chip
                                                        size="small"
                                                        label={<ExclamationMarkIcon />}
                                                        type={'partyType'}
                                                        isError
                                                    />
                                                </div>
                                            </Tooltip>
                                        )}
                                    {contactListItem.isTimecardAddingAutoBlocked ? (
                                        <Tooltip content={t('dashboard.tooltips.timecard-blocked-short')}>
                                            <div>
                                                <Chip size="small" label={<BlockIcon />} type={'partyType'} isError />
                                            </div>
                                        </Tooltip>
                                    ) : null}
                                    {contactListItem.partyType === PartyType.COMPANY ? (
                                        <Tooltip content={t('contacts.types.company')}>
                                            <div>
                                                <Chip size="small" label={<BusinessCenter />} type={'partyType'} />
                                            </div>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip content={t('contacts.types.person')}>
                                            <div>
                                                <Chip size="small" label={<Person />} type={'partyType'} />
                                            </div>
                                        </Tooltip>
                                    )}
                                    {contactListItem.relationshipTypeCode === RelationshipTypeCode.CLIENT ? (
                                        <Tooltip
                                            content={getClassifierText(classifiers['RELATIONSHIP_TYPE'], 'CLIENT')}
                                        >
                                            <div>
                                                <Chip size="small" label={<AttachMoney />} type={'partyType'} />
                                            </div>
                                        </Tooltip>
                                    ) : null}
                                    {contactListItem.relationshipTypeCode === RelationshipTypeCode.LEAD ? (
                                        <Tooltip content={getClassifierText(classifiers['RELATIONSHIP_TYPE'], 'LEAD')}>
                                            <div>
                                                <Chip
                                                    size="small"
                                                    label={<ArrowOutwardRoundedIcon />}
                                                    type={'partyType'}
                                                />
                                            </div>
                                        </Tooltip>
                                    ) : null}
                                    <OpponentChip state={contactListItem.opponentCaseState} />
                                </div>
                                <div className="flex gap-0.5">
                                    {contactListItem.segmentTypeCode != null && (
                                        <SegmentChip segmentTypeCode={contactListItem.segmentTypeCode} />
                                    )}
                                    {contactListItem.serviceLevelTypeCode != null && (
                                        <span className="text-subtitle2 text-grey-600 new:whitespace-nowrap flex items-center font-semibold">
                                            {getClassifierText(
                                                classifiers['SERVICE_LEVEL_TYPE'],
                                                contactListItem.serviceLevelTypeCode
                                            )}
                                        </span>
                                    )}
                                </div>
                                {!isNewDesign && contactListItem.dmsLink && !contactListItem.isDraft && (
                                    <div className="flex gap-0.5">
                                        <DmsLink link={contactListItem.dmsLink} isIconDisplayed={true} />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                },
            },
            {
                header: t('contacts.relationship-owners'),
                accessorKey: 'managers',
                enableSorting: false,
                meta: {
                    widthWeight: 1.2,
                    minPx: 160,
                },
                cell: ({ cell }) => {
                    return (
                        <RelationshipPartnersAndManagers
                            relationshipPartners={cell.row.original.partners?.map(({ employee }) => employee) || []}
                            relationshipManagers={cell.row.original.managers?.map(({ employee }) => employee) || []}
                        />
                    );
                },
            },
            {
                header: t('contacts.contact-details'),
                accessorKey: 'mainEmail',
                enableSorting: false,
                meta: {
                    widthWeight: 2.8,
                    minPx: 260,
                },
                cell: ({ cell }) => {
                    const { mainMobile, mainPhone, mainEmail, partyType } = cell.row.original;
                    return (
                        <div className="flex w-fit flex-col gap-1">
                            {mainEmail?.length ? (
                                <div>
                                    {t('email')}:&nbsp;
                                    <a className={LINK_CLASS} href={`mailto:${mainEmail}`}>
                                        {mainEmail}
                                    </a>
                                    <CopyToClipboard text={mainEmail} />
                                </div>
                            ) : null}
                            {mainMobile?.length && partyType === 'PERSON' ? (
                                <p>
                                    {t('mobile')}:&nbsp;{mainMobile}
                                </p>
                            ) : null}
                            {mainPhone?.length && partyType === 'COMPANY' ? (
                                <p>
                                    {t('phone')}:&nbsp;{mainPhone}
                                </p>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                header: t('contacts.employed-by'),
                id: 'employed',
                enableSorting: false,
                meta: {
                    widthWeight: 1.2,
                    minPx: 160,
                },
                cell: ({ cell }) => {
                    const relatesTo = getEmployers(cell.row?.original || []);
                    const links = relatesTo.map((relation: PartyToPartyRelationDto, index: number) => (
                        <Fragment key={`${relation.otherParty.id}-${relation.otherParty.name}`}>
                            <PartyName party={relation.otherParty} isLink variant="list" />
                            {index < relatesTo.length - 1 && <span>,&nbsp;</span>}
                        </Fragment>
                    ));
                    return <div>{links}</div>;
                },
            },
            {
                header: t('contacts.last-bd-effort'),
                id: 'lastBdDate',
                enableSorting: false,
                meta: {
                    widthWeight: 1.2,
                    // The new design's overdue cell holds an icon beside "12 months ago", on one line.
                    minPx: isNewDesign ? 134 : 120,
                },
                cell: ({ cell }) => {
                    const { text: timeSinceLastEffort, shouldHighlight } = calculateTimeSince(
                        cell.row.original.lastBdEffort,
                        cell.row.original.relationshipTypeCode,
                        dateFormat
                    );
                    return <TimeSinceCell text={timeSinceLastEffort} overdue={shouldHighlight} />;
                },
            },
            ...(lastEffortType
                ? [
                      {
                          header: lastEffortType.label,
                          id: 'lastHCSBdDate',
                          // The accessor is what makes the header sort: v8 gates getCanSort on one, manualSorting
                          // included. The id stays the backend's sort key.
                          accessorKey: 'lastHCSBdEffort',
                          meta: {
                              widthWeight: 1.2,
                              minPx: 120,
                              nullHandling: 'nullsLast' as const,
                          },
                          cell: ({ row }: CellContext<ContactListItem, unknown>) => (
                              <p className="text-body1">
                                  {calculateTimeSince(row.original.lastHCSBdEffort, null, dateFormat).text}
                              </p>
                          ),
                      },
                  ]
                : []),
        ],
        [lastEffortType, isNonVerifiedIconEnabled, classifiers, lastNameFirst, dateFormat, isNewDesign]
    );

    // Rebuilt on every tick, which is also what re-renders the table body's row shading.
    const columnsWithSelect = useMemo<DataTableColumnDef<ContactListItem>[]>(
        () =>
            canMergeContacts
                ? [
                      {
                          id: 'select',
                          enableSorting: false,
                          meta: { fixedPx: 64 },
                          // A draft is merged into nothing: it is created first.
                          cell: ({ row }: CellContext<ContactListItem, unknown>) =>
                              row.original.isDraft ? null : (
                                  <Checkbox
                                      checked={selected.has(row.original.id)}
                                      onCheckedChange={() => toggleSelected(row.original)}
                                      aria-label={t('contacts.merge.select-row', { name: row.original.name })}
                                  />
                              ),
                      },
                      ...columns,
                  ]
                : columns,
        [canMergeContacts, columns, selected, toggleSelected]
    );

    const relationshipTypeOptions = useMemo(
        () => mapToFilterOptions(filterRelationshipTypes, classifiers['RELATIONSHIP_TYPE']),
        [classifiers]
    );

    const referredBySourceTypeOptions = useMemo(
        () => mapToFilterOptions(filterReferredBySourceTypes, classifiers['REFERRAL_TYPE']),
        [classifiers]
    );

    const filterConfig: FilterConfig = useMemo(() => {
        const shared = contactFilters({
            classifiers,
            relationshipTypeOptions,
            activityType: lastEffortType?.name,
            clientAssistantLabel,
        });
        return {
            quickFilters: [
                {
                    selector: 'id',
                    comperator: '=ismyclient=',
                    filters: [
                        {
                            tooltip: 'contacts.tooltips.my-clients',
                            label: t('contacts.my-clients'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    selector: 'statusCode',
                    comperator: '=ismycontact=',
                    filters: [
                        {
                            tooltip: 'contacts.tooltips.my-contacts',
                            label: t('contacts.my-contacts'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    label: 'contacts.filter.show',
                    selector: 'partyType',
                    filters: [
                        {
                            label: t('contacts.types.company'),
                            argument: 'COMPANY',
                        },
                        {
                            label: t('contacts.types.person'),
                            argument: 'PERSON',
                        },
                    ],
                },
                {
                    label: 'contacts.filter.relationship',
                    selector: 'relationshipTypeCode',
                    multiple: true,
                    filters: relationshipTypeOptions,
                },
            ],
            filters: [
                // contact section
                shared.myClients,
                shared.myContacts,
                shared.name,
                {
                    label: t('contacts.filter.contact-type'),
                    type: 'checkboxgroup',
                    selector: 'partyType',
                    group: 'contacts.contact',
                    filters: [
                        {
                            label: t('contacts.types.company'),
                            argument: 'COMPANY',
                        },
                        {
                            label: t('contacts.types.person'),
                            argument: 'PERSON',
                        },
                    ],
                },
                shared.relationshipType,
                shared.internalClient,
                ...(isInvoicingDateOnPartyFormEnabled ? [shared.invoiceDateCurrentDate] : []),
                shared.relationshipPartner,
                shared.relationshipManager,
                ...(isAdministratorFieldEnabled ? [shared.clientAssistant] : []),
                shared.businessArea,
                shared.sectorGroup,
                shared.segment,
                shared.serviceLevel,
                shared.employer,
                {
                    type: 'dynamicmultiselect',
                    subType: 'contact',
                    label: t('contacts.contact-employee'),
                    selector: 'id',
                    comperator: '=isemployeecontact=',
                    group: 'contacts.contact',
                    tooltip: 'contacts.tooltips.contact-employee',
                    canExclude: true,
                    paginated: {
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        filters: `partyType==${PartyType.PERSON}`,
                        searchField: ['name', 'partyNumber'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                shared.country,
                shared.city,
                ...(hasSeveralActiveOffices
                    ? ([
                          shared.casesHandledByOffice,
                          shared.noRecentWorkByOffice,
                          shared.neverServedByOffice,
                      ] satisfies MainFilter[])
                    : []),
                shared.casesInPracticeGroup,
                shared.noRecentWorkByPracticeGroup,
                shared.neverServedByPracticeGroup,
                shared.memberOfGroup,
                shared.cooperationPartner,
                shared.keywords,
                shared.notes,
                shared.createdDate,
                shared.clientSince,
                shared.caseLastTimecardDate,
                shared.lastBdEffortFrom,
                shared.lastBdEffortOlderThan,
                ...(canReadReferrals
                    ? ([
                          {
                              type: 'checkboxgroup',
                              label: t('contacts.filter.referred-by-source'),
                              group: 'contacts.contact',
                              selector: 'referrals.referralTypeCode',
                              multiple: true,
                              filters: referredBySourceTypeOptions,
                          },
                          {
                              type: 'dynamicmultiselect',
                              subType: 'contact',
                              label: t('contacts.filter.referred-by-name'),
                              group: 'contacts.contact',
                              selector: 'id',
                              comperator: '=isreferredparty=',
                              paginated: {
                                  fetch: useLazyGetMinContactsListWithFiltersQuery,
                                  searchField: ['name', 'partyNumber'],
                                  getOptionIdentifier: 'id',
                                  getOptionLabel: 'name',
                              },
                          },
                          {
                              type: 'dynamicmultiselect',
                              label: t('contacts.filter.referred-by-colleague'),
                              group: 'contacts.contact',
                              selector: 'referrals.employee.id',
                              conditions: { not: true, notComparator: '=notany=', empty: true },
                              paginated: {
                                  fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                                  searchField: 'fullName',
                                  getOptionIdentifier: 'id',
                                  getOptionLabel: 'name',
                              },
                          },
                      ] satisfies MainFilter[])
                    : []),
                ...(lastEffortType
                    ? ([shared.lastEffortActivityFrom, shared.lastEffortActivityOlderThan] satisfies MainFilter[])
                    : []),
                // person-specific filters section
                shared.businessContactOf,
                shared.mailingPreferences,
                shared.marketingAreas,
                shared.preferredMailingLanguage,
                shared.jurisdictions,
                shared.employerCountry,
                shared.employerBusinessArea,
                shared.employerSegment,
                shared.employerServiceLevel,
                shared.employerKeyword,
                shared.position,
                shared.areasOfResponsibility,
                shared.excludeEmployedByInternalClient,
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=excludewithoutemail=',
                    group: 'contacts.person-filters.title',
                    filters: [
                        {
                            tooltip: 'contacts.tooltips.exclude-without-email',
                            label: t('contacts.person-filters.exclude-without-email'),
                            argument: 'true',
                        },
                    ],
                },
                shared.excludePersonaNonGrata,
                // connected cases sectioon
                shared.caseStatus,
                shared.caseSupervisingPartner,
                shared.caseResponsibleLawyer,
                shared.caseOpeningDate,
                // `true` is band 1's argument so a saved `id=isopponent=true` still ticks it
                shared.opponent,
                // other section
                shared.selectedAsJudge,
                shared.activeCasesNoAgreement,
                shared.inactiveRelationshipPartner,
                shared.inactiveRelationshipManager,
                shared.addingCasesBlocked,
                shared.addingTimecardsBlocked,
            ],
            searchBarSelectors: [
                { selector: 'name' },
                { selector: 'partyNumber' },
                { selector: 'mainEmail' },
                { selector: 'mainPhone', comparator: '=phonelk=' },
                { selector: 'mainMobile', comparator: '=phonelk=' },
                { selector: 'partners.employee.fullName' },
                { selector: 'managers.employee.fullName' },
                { selector: 'id', comparator: '=hasemployernamelk=' },
            ],
        };
    }, [
        classifiers,
        relationshipTypeOptions,
        referredBySourceTypeOptions,
        canReadReferrals,
        lastEffortType,
        isInvoicingDateOnPartyFormEnabled,
        isAdministratorFieldEnabled,
        clientAssistantLabel,
        hasSeveralActiveOffices,
    ]);

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<ContactListItem>
            getRowHref={getContactHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="contacts"
            columns={columnsWithSelect}
            data={data}
            isLoading={isLoadingClassifiers || isFetching}
            // Classic keeps its empty state over a failed list.
            isError={isNewDesign && isError}
            pluralTextId="contacts.contacts"
            singularTextId="contacts.contact"
            noEntriesId={isNewDesign ? 'contacts.empty.title' : 'contacts.no-contacts-found'}
            noEntriesBodyId="contacts.empty.body"
            noEntriesIcon={GroupOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.PARTIES', 'CREATE.PARTIES.CLIENTS', 'CREATE.PARTIES.LEADS_AND_NON_CLIENTS']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn('/contact/new')}
                    labelId="contacts.add-new-contact"
                    newLabelId="new-entity.contact"
                />
            }
            selectedIds={selectedIds}
            onSelectionChange={handleSelectionChange}
            highlightSelection
            belowQuickFilters={
                canMergeContacts ? <ContactsSelectionBar selected={selected} onClear={clearSelection} /> : null
            }
            fetchData={fetchData}
            fetchCount={useLazyContactsCountQuery}
            filterConfig={filterConfig}
            rowsPerPage={50}
            exportHookResults={
                disableExport || !hasPermissionToExport
                    ? undefined
                    : [
                          {
                              labelKey: 'export',
                              isBusy: isExportDataFetching,
                              triggerManualDownload,
                          },
                      ]
            }
        />
    );
};

export default ContactsTable;
